/**
 * API Service for Wingbeat Paragliding Club
 * Handles all API communication with backend Lambda functions
 */
import { fetchAuthSession } from '@aws-amplify/auth';

// API base URL - will be configured via environment variables
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api-dev.wingbeat.example.com';

/**
 * Authorization header for authenticated requests. Returns {} if not signed in.
 *
 * Uses the Cognito **ID token** (not the access token): the API Gateway
 * COGNITO_USER_POOLS authorizer validates the token's `aud` claim against the
 * app client ID. ID tokens carry `aud`; access tokens do not (they use
 * `client_id`/`token_use: access`), so sending the access token is rejected 401.
 */
export const authHeaders = async () => {
  try {
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
};

/**
 * Generic API request handler with enhanced error handling and retry logic
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  // Always attach the Cognito auth header when signed in. authHeaders() returns
  // {} when not authenticated, so this is a no-op for anonymous/public calls and
  // harmless on public routes (the header is simply unused). This means every
  // authenticated endpoint works without each caller remembering to pass it -
  // fixes a class of 401s on /admin and other authorizer-protected routes.
  const auth = await authHeaders();

  const config = {
    timeout: 10000, // 10 second timeout
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...auth,
      ...options.headers, // caller-supplied headers win (e.g. explicit auth)
    },
  };

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout);
    
    const response = await fetch(url, {
      ...config,
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      throw new Error('Invalid response format from server', { cause: parseError });
    }
    
    if (!response.ok) {
      const errorMessage = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }
    
    return data;
  } catch (error) {
    console.error('API Error:', {
      endpoint,
      error: error.message,
      url,
    });
    
    // Handle specific error types
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - please try again', { cause: error });
    }
    
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw new Error('No internet connection - please check your network', { cause: error });
    }
    
    throw error;
  }
};

/**
 * Homepage API with enhanced validation and error handling
 */
export const homepageAPI = {
  // Get all homepage content
  getContent: async () => {
    try {
      const response = await apiRequest('/homepage');
      return response;
    } catch (error) {
      console.error('Failed to fetch homepage content:', error);
      throw new Error(`Failed to load homepage content: ${error.message}`, { cause: error });
    }
  },
  
  // List all content items
  listContent: async () => {
    try {
      const response = await apiRequest('/homepage/content');
      return response;
    } catch (error) {
      console.error('Failed to fetch content list:', error);
      throw new Error(`Failed to load content list: ${error.message}`, { cause: error });
    }
  },
  
  // Get specific content by type
  getContentByType: async (contentType) => {
    if (!contentType || typeof contentType !== 'string') {
      throw new Error('Content type is required and must be a string');
    }
    
    try {
      const response = await apiRequest(`/homepage/content/${encodeURIComponent(contentType)}`);
      return response;
    } catch (error) {
      console.error(`Failed to fetch ${contentType} content:`, error);
      throw new Error(`Failed to load ${contentType} content: ${error.message}`, { cause: error });
    }
  },
  
  // Create or update content
  updateContent: async (contentType, title, content) => {
    // Validation
    if (!contentType || typeof contentType !== 'string') {
      throw new Error('Content type is required and must be a string');
    }
    if (!title || typeof title !== 'string' || title.trim().length === 0) {
      throw new Error('Title is required and cannot be empty');
    }
    if (!content || typeof content !== 'string' || content.trim().length === 0) {
      throw new Error('Content is required and cannot be empty');
    }
    
    try {
      const response = await apiRequest('/homepage/content', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ 
          contentType: contentType.trim(), 
          title: title.trim(), 
          content: content.trim() 
        }),
      });
      return response;
    } catch (error) {
      console.error('Failed to update content:', error);
      throw new Error(`Failed to update content: ${error.message}`, { cause: error });
    }
  },
  
  // Update specific content by type (PUT method)
  updateContentByType: async (contentType, title, content) => {
    // Validation
    if (!contentType || typeof contentType !== 'string') {
      throw new Error('Content type is required and must be a string');
    }
    if (!title || typeof title !== 'string' || title.trim().length === 0) {
      throw new Error('Title is required and cannot be empty');
    }
    if (!content || typeof content !== 'string' || content.trim().length === 0) {
      throw new Error('Content is required and cannot be empty');
    }
    
    try {
      const response = await apiRequest(`/homepage/content/${encodeURIComponent(contentType)}`, {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify({ 
          title: title.trim(), 
          content: content.trim() 
        }),
      });
      return response;
    } catch (error) {
      console.error(`Failed to update ${contentType} content:`, error);
      throw new Error(`Failed to update ${contentType} content: ${error.message}`, { cause: error });
    }
  },

  // Get hero banner images
  getHeroImages: async () => {
    try {
      const response = await apiRequest('/homepage/hero-images');
      return response;
    } catch (error) {
      console.error('Failed to fetch hero images:', error);
      throw new Error(`Failed to load hero images: ${error.message}`, { cause: error });
    }
  },

  // Add hero banner image (admin only)
  addHeroImage: async (imageData) => {
    // Validation
    if (!imageData.imageUrl || typeof imageData.imageUrl !== 'string') {
      throw new Error('Image URL is required and must be a string');
    }

    try {
      const response = await apiRequest('/homepage/hero-images', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(imageData),
      });
      return response;
    } catch (error) {
      console.error('Failed to add hero image:', error);
      throw new Error(`Failed to add hero image: ${error.message}`, { cause: error });
    }
  },

  // Update hero banner image (admin only)
  updateHeroImage: async (imageId, imageData) => {
    if (!imageId || typeof imageId !== 'string') {
      throw new Error('Image ID is required and must be a string');
    }
    if (!imageData.imageUrl || typeof imageData.imageUrl !== 'string') {
      throw new Error('Image URL is required and must be a string');
    }

    try {
      const response = await apiRequest(`/homepage/hero-images/${encodeURIComponent(imageId)}`, {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify(imageData),
      });
      return response;
    } catch (error) {
      console.error(`Failed to update hero image ${imageId}:`, error);
      throw new Error(`Failed to update hero image: ${error.message}`, { cause: error });
    }
  },

  // Delete hero banner image (admin only)
  deleteHeroImage: async (imageId) => {
    if (!imageId || typeof imageId !== 'string') {
      throw new Error('Image ID is required and must be a string');
    }

    try {
      const response = await apiRequest(`/homepage/hero-images/${encodeURIComponent(imageId)}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      });
      return response;
    } catch (error) {
      console.error(`Failed to delete hero image ${imageId}:`, error);
      throw new Error(`Failed to delete hero image: ${error.message}`, { cause: error });
    }
  },

  // Get current seasonal message
  getSeasonalMessage: async () => {
    try {
      const response = await apiRequest('/homepage/seasonal-message');
      return response;
    } catch (error) {
      console.error('Failed to fetch seasonal message:', error);
      throw new Error(`Failed to load seasonal message: ${error.message}`, { cause: error });
    }
  },

  // Enhanced content management endpoints
  
  // Get content by ID
  getContentById: async (contentId) => {
    if (!contentId || typeof contentId !== 'string') {
      throw new Error('Content ID is required and must be a string');
    }
    
    try {
      const response = await apiRequest(`/homepage/content/id/${encodeURIComponent(contentId)}`);
      return response;
    } catch (error) {
      console.error(`Failed to fetch content ${contentId}:`, error);
      throw new Error(`Failed to load content: ${error.message}`, { cause: error });
    }
  },

  // Create new content
  createContent: async (contentData) => {
    // Validation
    if (!contentData.contentType || typeof contentData.contentType !== 'string') {
      throw new Error('Content type is required and must be a string');
    }
    if (!contentData.title || typeof contentData.title !== 'string' || contentData.title.trim().length === 0) {
      throw new Error('Title is required and cannot be empty');
    }
    if (!contentData.content || typeof contentData.content !== 'string' || contentData.content.trim().length === 0) {
      throw new Error('Content is required and cannot be empty');
    }
    
    try {
      const response = await apiRequest('/homepage/content', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(contentData),
      });
      return response;
    } catch (error) {
      console.error('Failed to create content:', error);
      throw new Error(`Failed to create content: ${error.message}`, { cause: error });
    }
  },

  // Update content by ID
  updateContentById: async (contentId, contentData) => {
    if (!contentId || typeof contentId !== 'string') {
      throw new Error('Content ID is required and must be a string');
    }
    
    try {
      const response = await apiRequest(`/homepage/content/id/${encodeURIComponent(contentId)}`, {
        method: 'PUT',
        body: JSON.stringify(contentData),
      });
      return response;
    } catch (error) {
      console.error(`Failed to update content ${contentId}:`, error);
      throw new Error(`Failed to update content: ${error.message}`, { cause: error });
    }
  },

  // Delete content by ID
  deleteContent: async (contentId) => {
    if (!contentId || typeof contentId !== 'string') {
      throw new Error('Content ID is required and must be a string');
    }
    
    try {
      const response = await apiRequest(`/homepage/content/id/${encodeURIComponent(contentId)}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      console.error(`Failed to delete content ${contentId}:`, error);
      throw new Error(`Failed to delete content: ${error.message}`, { cause: error });
    }
  },

  // Archive content
  archiveContent: async (contentId) => {
    if (!contentId || typeof contentId !== 'string') {
      throw new Error('Content ID is required and must be a string');
    }
    
    try {
      const response = await apiRequest(`/homepage/content/id/${encodeURIComponent(contentId)}/archive`, {
        method: 'PATCH',
      });
      return response;
    } catch (error) {
      console.error(`Failed to archive content ${contentId}:`, error);
      throw new Error(`Failed to archive content: ${error.message}`, { cause: error });
    }
  },

  // Bulk operations
  bulkUpdateContent: async (contentIds, updateData) => {
    if (!Array.isArray(contentIds) || contentIds.length === 0) {
      throw new Error('Content IDs array is required and cannot be empty');
    }
    if (!updateData || typeof updateData !== 'object') {
      throw new Error('Update data is required and must be an object');
    }
    
    try {
      const response = await apiRequest('/homepage/content/bulk', {
        method: 'PATCH',
        body: JSON.stringify({ contentIds, updateData }),
      });
      return response;
    } catch (error) {
      console.error('Failed to bulk update content:', error);
      throw new Error(`Failed to bulk update content: ${error.message}`, { cause: error });
    }
  },

  // Get content analytics
  getContentAnalytics: async () => {
    try {
      const response = await apiRequest('/homepage/content/analytics');
      return response;
    } catch (error) {
      console.error('Failed to fetch content analytics:', error);
      throw new Error(`Failed to load content analytics: ${error.message}`, { cause: error });
    }
  },
};

/**
 * Sites API with comprehensive CRUD operations and filtering
 */
export const sitesAPI = {
  // Get all sites with optional filtering
  getAllSites: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add filters to query parameters
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.direction) queryParams.append('direction', filters.direction);
      if (filters.minHeight) queryParams.append('minHeight', filters.minHeight);
      if (filters.maxHeight) queryParams.append('maxHeight', filters.maxHeight);
      if (filters.search) queryParams.append('search', filters.search);
      
      const endpoint = `/sites${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiRequest(endpoint);
      
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      throw new Error(`Failed to load sites: ${error.message}`, { cause: error });
    }
  },
  
  // Get sites data optimized for map display
  getSitesForMap: async () => {
    try {
      const response = await apiRequest('/sites/map');
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch sites for map:', error);
      throw new Error(`Failed to load map data: ${error.message}`, { cause: error });
    }
  },
  
  // Get specific site by ID
  getSiteById: async (siteId) => {
    if (!siteId || typeof siteId !== 'string') {
      throw new Error('Site ID is required and must be a string');
    }
    
    try {
      const response = await apiRequest(`/sites/${encodeURIComponent(siteId)}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch site ${siteId}:`, error);
      if (error.message.includes('404')) {
        throw new Error('Site not found', { cause: error });
      }
      throw new Error(`Failed to load site details: ${error.message}`, { cause: error });
    }
  },
  
  // Create new site (admin only)
  createSite: async (siteData) => {
    // Validation
    const required = ['name', 'direction', 'height', 'latitude', 'longitude'];
    const missing = required.filter(field => !siteData[field]);
    
    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }
    
    try {
      const response = await apiRequest('/sites', {
        method: 'POST',
        body: JSON.stringify(siteData),
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create site:', error);
      throw new Error(`Failed to create site: ${error.message}`, { cause: error });
    }
  },
  
  // Update existing site (admin only)
  updateSite: async (siteId, siteData) => {
    if (!siteId || typeof siteId !== 'string') {
      throw new Error('Site ID is required and must be a string');
    }
    
    try {
      const response = await apiRequest(`/sites/${encodeURIComponent(siteId)}`, {
        method: 'PUT',
        body: JSON.stringify(siteData),
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to update site ${siteId}:`, error);
      if (error.message.includes('404')) {
        throw new Error('Site not found', { cause: error });
      }
      throw new Error(`Failed to update site: ${error.message}`, { cause: error });
    }
  },
  
  // Delete site (admin only)
  deleteSite: async (siteId) => {
    if (!siteId || typeof siteId !== 'string') {
      throw new Error('Site ID is required and must be a string');
    }
    
    try {
      const response = await apiRequest(`/sites/${encodeURIComponent(siteId)}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      console.error(`Failed to delete site ${siteId}:`, error);
      if (error.message.includes('404')) {
        throw new Error('Site not found', { cause: error });
      }
      throw new Error(`Failed to delete site: ${error.message}`, { cause: error });
    }
  },
};

/**
 * Admin API for comprehensive site management
 */
export const adminAPI = {
  // Get site analytics
  getSiteAnalytics: async () => {
    try {
      const response = await apiRequest('/admin/sites/analytics', {
        headers: await authHeaders(),
      });
      return response.data || {};
    } catch (error) {
      console.error('Failed to fetch site analytics:', error);
      throw new Error(`Failed to load site analytics: ${error.message}`, { cause: error });
    }
  },

  // Flight logs awaiting approval (admin moderation queue)
  getPendingFlightLogs: async () => {
    const response = await apiRequest('/admin/flightlogs/pending', {
      headers: await authHeaders(),
    });
    return response.data || [];
  },
  
  // Bulk update sites
  bulkUpdateSites: async (siteIds, updateData) => {
    if (!Array.isArray(siteIds) || siteIds.length === 0) {
      throw new Error('Site IDs array is required and cannot be empty');
    }
    if (!updateData || typeof updateData !== 'object') {
      throw new Error('Update data is required and must be an object');
    }
    
    try {
      const response = await apiRequest('/admin/sites/bulk-update', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ siteIds, updateData }),
      });
      return response;
    } catch (error) {
      console.error('Failed to bulk update sites:', error);
      throw new Error(`Failed to bulk update sites: ${error.message}`, { cause: error });
    }
  },
  
  // Export site data
  exportSiteData: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add filters to query parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const endpoint = `/admin/sites/export${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiRequest(endpoint, {
        headers: await authHeaders(),
      });
      
      return response.data || [];
    } catch (error) {
      console.error('Failed to export site data:', error);
      throw new Error(`Failed to export site data: ${error.message}`, { cause: error });
    }
  },
  
  // Update site status
  updateSiteStatus: async (siteId, status) => {
    if (!siteId || typeof siteId !== 'string') {
      throw new Error('Site ID is required and must be a string');
    }
    if (!status || typeof status !== 'string') {
      throw new Error('Status is required and must be a string');
    }
    
    try {
      const response = await apiRequest(`/admin/sites/${encodeURIComponent(siteId)}/status`, {
        method: 'PATCH',
        headers: await authHeaders(),
        body: JSON.stringify({ status }),
      });
      return response;
    } catch (error) {
      console.error(`Failed to update site status for ${siteId}:`, error);
      throw new Error(`Failed to update site status: ${error.message}`, { cause: error });
    }
  },
  
  // Update site publishing status
  updateSitePublishing: async (siteId, published) => {
    if (!siteId || typeof siteId !== 'string') {
      throw new Error('Site ID is required and must be a string');
    }
    if (typeof published !== 'boolean') {
      throw new Error('Published status must be a boolean');
    }
    
    try {
      const response = await apiRequest(`/admin/sites/${encodeURIComponent(siteId)}/publish`, {
        method: 'PATCH',
        headers: await authHeaders(),
        body: JSON.stringify({ published }),
      });
      return response;
    } catch (error) {
      console.error(`Failed to update site publishing for ${siteId}:`, error);
      throw new Error(`Failed to update site publishing: ${error.message}`, { cause: error });
    }
  },

  // List contact/join form submissions. kind: '' | 'contact' | 'join'
  getSubmissions: async (kind = '') => {
    const q = kind ? `?kind=${encodeURIComponent(kind)}` : '';
    const response = await apiRequest(`/admin/submissions${q}`, {
      headers: await authHeaders(),
    });
    return response.submissions || [];
  },

  // Get the contact-notification recipient config
  getContactConfig: async () => {
    const response = await apiRequest('/admin/contact-config', {
      headers: await authHeaders(),
    });
    return response.recipients || [];
  },

  // Set the contact-notification recipients (array of emails)
  setContactConfig: async (recipients) => {
    const response = await apiRequest('/admin/contact-config', {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify({ recipients }),
    });
    return response.recipients || [];
  },
};

/**
 * Flight Logs API for browsing and managing flight stories
 */
export const flightLogsAPI = {
  // Get published flight logs (paginated)
  getFlightLogs: async (page = 1, limit = 10, filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.author) queryParams.append('author', filters.author);
      if (filters.year) queryParams.append('year', filters.year);
      if (filters.site) queryParams.append('site', filters.site);

      const response = await apiRequest(`/flightlogs?${queryParams.toString()}`);
      return response;
    } catch (error) {
      console.error('Failed to fetch flight logs:', error);
      throw new Error(`Failed to load flight logs: ${error.message}`, { cause: error });
    }
  },

  // Get recent flight logs (for homepage)
  getRecentLogs: async (count = 5) => {
    try {
      const response = await apiRequest(`/flightlogs/recent?count=${count}`);
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch recent flight logs:', error);
      throw new Error(`Failed to load recent flight logs: ${error.message}`, { cause: error });
    }
  },

  // Get single flight log by ID
  getFlightLog: async (logId) => {
    if (!logId || typeof logId !== 'string') {
      throw new Error('Log ID is required and must be a string');
    }

    try {
      const response = await apiRequest(`/flightlogs/${encodeURIComponent(logId)}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch flight log ${logId}:`, error);
      if (error.message.includes('404')) {
        throw new Error('Flight log not found', { cause: error });
      }
      throw new Error(`Failed to load flight log: ${error.message}`, { cause: error });
    }
  },

  // Create new flight log (authenticated)
  createFlightLog: async (logData) => {
    if (!logData.title || typeof logData.title !== 'string' || logData.title.trim().length === 0) {
      throw new Error('Title is required and cannot be empty');
    }
    if (!logData.content && !logData.introtext) {
      throw new Error('Content is required');
    }

    try {
      const response = await apiRequest('/flightlogs', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(logData),
      });
      return response;
    } catch (error) {
      console.error('Failed to create flight log:', error);
      throw new Error(`Failed to create flight log: ${error.message}`, { cause: error });
    }
  },

  // Update flight log (authenticated, own logs only)
  updateFlightLog: async (logId, logData) => {
    if (!logId || typeof logId !== 'string') {
      throw new Error('Log ID is required');
    }

    try {
      const response = await apiRequest(`/flightlogs/${encodeURIComponent(logId)}`, {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify(logData),
      });
      return response;
    } catch (error) {
      console.error(`Failed to update flight log ${logId}:`, error);
      throw new Error(`Failed to update flight log: ${error.message}`, { cause: error });
    }
  },

  // Delete flight log (authenticated, own logs or admin)
  deleteFlightLog: async (logId) => {
    if (!logId || typeof logId !== 'string') {
      throw new Error('Log ID is required');
    }

    try {
      const response = await apiRequest(`/flightlogs/${encodeURIComponent(logId)}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      });
      return response;
    } catch (error) {
      console.error(`Failed to delete flight log ${logId}:`, error);
      throw new Error(`Failed to delete flight log: ${error.message}`, { cause: error });
    }
  },

  // Set a flight log's status (admin only), e.g. publish a pending log.
  // status: 'PUBLISHED' | 'PENDING_APPROVAL' | 'DRAFT'
  setFlightLogStatus: async (logId, status) => {
    if (!logId || typeof logId !== 'string') {
      throw new Error('Log ID is required');
    }
    try {
      const response = await apiRequest(`/flightlogs/${encodeURIComponent(logId)}/status`, {
        method: 'PATCH',
        headers: await authHeaders(),
        body: JSON.stringify({ status }),
      });
      return response;
    } catch (error) {
      console.error(`Failed to set flight log status ${logId}:`, error);
      throw new Error(`Failed to update status: ${error.message}`, { cause: error });
    }
  },
};

// Public contact / join-the-club form submissions
export const contactAPI = {
  // kind: 'contact' | 'join'. payload includes honeypot field _hp.
  submit: async (kind, payload) => {
    const endpoint = kind === 'join' ? '/contact/join' : '/contact';
    const response = await apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response;
  },
};

// Public news articles
export const newsAPI = {
  list: async () => {
    const response = await apiRequest('/news');
    return response.data || [];
  },
  getById: async (id) => {
    const response = await apiRequest(`/news/${encodeURIComponent(id)}`);
    return response.data || null;
  },
  // Admin: list ALL news incl. drafts. Served by the admin Lambda (behind the
  // Cognito authorizer) — the public /news GET routes are unauthenticated, so a
  // draft-inclusive listing must go through /admin/*.
  listAdmin: async () => {
    const response = await apiRequest('/admin/news', { headers: await authHeaders() });
    return response.data || [];
  },
  // Admin: create an article. status: 'DRAFT' | 'PUBLISHED' (defaults DRAFT).
  create: async ({ title, content, status = 'DRAFT', excerpt, featured_image }) => {
    return apiRequest('/news', {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ title, content, status, excerpt, featured_image }),
    });
  },
  // Admin: update an article by id.
  update: async (id, fields) => {
    return apiRequest(`/news/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify(fields),
    });
  },
  // Admin: publish/unpublish. status: 'PUBLISHED' | 'DRAFT'.
  setStatus: async (id, status) => {
    return apiRequest(`/news/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      headers: await authHeaders(),
      body: JSON.stringify({ status }),
    });
  },
  // Admin: delete an article.
  remove: async (id) => {
    return apiRequest(`/news/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: await authHeaders(),
    });
  },
};

// Media uploads (authenticated): presigned S3 URLs + gallery manifest
export const mediaAPI = {
  // files: [{ name, type }] -> { folder, uploads: [{ name, fullUrl, thumbUrl }] }
  presign: async (date, site, files) => {
    return apiRequest('/media/presign', {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ date, site, files }),
    });
  },
  // Merge uploaded filenames into the folder's manifest.json
  updateManifest: async (folder, filenames) => {
    return apiRequest('/media/manifest', {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ folder, filenames }),
    });
  },
};

export default {
  homepage: homepageAPI,
  sites: sitesAPI,
  admin: adminAPI,
  flightLogs: flightLogsAPI,
  contact: contactAPI,
  news: newsAPI,
  media: mediaAPI,
};
